import React, { useState } from 'react';
import { IconCheck, IconEye, IconEyeOff, IconLock, IconPhone, IconUser } from '@tabler/icons-react';
import { InputMask } from '@react-input/mask';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from '../../../features/auth';
import { pb } from '../../../shared/api/pocketbase';
import { getPbErrorMessage } from '../../../shared/lib/pbError';
import { PHONE_MASK_OPTIONS } from '../../../shared/lib/phone';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Notify, useNotify } from '../../../shared/ui/Notify/Notify';
import { Topbar } from '../../../widgets/topbar';
import { MainSidebar, useSidebar } from '../../../widgets/main-sidebar';

interface ProfileForm {
  name: string;
  phone: string;
}

interface PasswordForm {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
}

const ProfilePage: React.FC = () => {
  const { currentUser, logout, refreshUser } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();
  const { msg, notify, clear } = useNotify();
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileForm>({
    defaultValues: { name: currentUser?.name ?? '', phone: currentUser?.phone ?? '' },
  });

  const onProfileSubmit = handleProfileSubmit(async values => {
    if (!currentUser) return;
    setSavingProfile(true);
    try {
      await pb.collection('users').update(currentUser.id, {
        name: values.name.trim(),
        phone: values.phone.trim(),
      });
      await refreshUser();
      logAction('Updated profile', 'EDIT');
      notify('Профиль обновлён');
    } catch (err) {
      notify(getPbErrorMessage(err, 'Ошибка при сохранении профиля'));
    } finally {
      setSavingProfile(false);
    }
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<PasswordForm>({
    defaultValues: { oldPassword: '', password: '', passwordConfirm: '' },
  });

  const newPassword = watch('password');

  const onSubmit = handleSubmit(async values => {
    if (!currentUser) return;
    setSaving(true);
    try {
      await pb.collection('users').update(currentUser.id, {
        oldPassword: values.oldPassword,
        password: values.password,
        passwordConfirm: values.passwordConfirm,
      });
      logAction('Changed password', 'EDIT');
      notify('Пароль успешно изменён');
      reset();
    } catch (err) {
      notify(getPbErrorMessage(err, 'Ошибка при смене пароля'));
    } finally {
      setSaving(false);
    }
  });

  if (!currentUser) return null;

  return (
    <div id="app-screen">
      <Topbar user={currentUser} onLogout={logout} onSidebarToggle={toggleSidebar} />
      <div className="app-body">
        <MainSidebar isAdmin={currentUser.role === 'ADMIN'} isManager={currentUser.role === 'MANAGER'} />
        <div className="app-content">
      <div className="profile-wrap">
        <div className="profile-card">
          <div className="profile-card-title">Настройки профиля</div>

          <div className="profile-section">
            <div className="profile-section-label">Личные данные</div>
            <form onSubmit={onProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="adm-field">
                <label className="adm-label">
                  Имя <span className="adm-req">*</span>
                </label>
                <Controller
                  control={profileControl}
                  name="name"
                  rules={{ required: 'Введите имя' }}
                  render={({ field }) => (
                    <div className="adm-input-wrap">
                      <IconUser size={15} stroke={2} className="adm-input-icon" />
                      <Input {...field} className="adm-input" placeholder="Иван Иванов" autoComplete="off" />
                    </div>
                  )}
                />
                {profileErrors.name && <div className="form-field-error">{profileErrors.name.message}</div>}
              </div>
              <div className="adm-field">
                <label className="adm-label">Телефон</label>
                <Controller
                  control={profileControl}
                  name="phone"
                  render={({ field }) => (
                    <div className="adm-input-wrap">
                      <IconPhone size={15} stroke={2} className="adm-input-icon" />
                      <InputMask
                        className="adm-input"
                        mask={PHONE_MASK_OPTIONS.mask}
                        replacement={PHONE_MASK_OPTIONS.replacement}
                        placeholder="+7 (___) ___-__-__"
                        type="tel"
                        autoComplete="off"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="adm-save" type="submit" disabled={savingProfile}>
                  {savingProfile ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
                  Сохранить
                </Button>
              </div>
            </form>
          </div>

          <div className="profile-section">
            <div className="profile-section-label">Смена пароля</div>
            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="adm-field">
                <label className="adm-label">
                  Текущий пароль <span className="adm-req">*</span>
                </label>
                <Controller
                  control={control}
                  name="oldPassword"
                  rules={{ required: 'Введите текущий пароль' }}
                  render={({ field }) => (
                    <div className="adm-input-wrap">
                      <IconLock size={15} stroke={2} className="adm-input-icon" />
                      <Input
                        {...field}
                        type={showOld ? 'text' : 'password'}
                        className="adm-input adm-input-has-suffix"
                        placeholder="Текущий пароль"
                        autoComplete="current-password"
                      />
                      <span className="adm-input-suffix">
                        <button
                          type="button"
                          className="adm-pwd-btn"
                          onClick={() => setShowOld(v => !v)}
                        >
                          {showOld ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                        </button>
                      </span>
                    </div>
                  )}
                />
                {errors.oldPassword && <div className="form-field-error">{errors.oldPassword.message}</div>}
              </div>

              <div className="adm-field">
                <label className="adm-label">
                  Новый пароль <span className="adm-req">*</span>
                </label>
                <Controller
                  control={control}
                  name="password"
                  rules={{
                    required: 'Введите новый пароль',
                    minLength: { value: 8, message: 'Минимум 8 символов' },
                  }}
                  render={({ field }) => (
                    <div className="adm-input-wrap">
                      <IconLock size={15} stroke={2} className="adm-input-icon" />
                      <Input
                        {...field}
                        type={showNew ? 'text' : 'password'}
                        className="adm-input adm-input-has-suffix"
                        placeholder="Минимум 8 символов"
                        autoComplete="new-password"
                      />
                      <span className="adm-input-suffix">
                        <button
                          type="button"
                          className="adm-pwd-btn"
                          onClick={() => setShowNew(v => !v)}
                        >
                          {showNew ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                        </button>
                      </span>
                    </div>
                  )}
                />
                {errors.password && <div className="form-field-error">{errors.password.message}</div>}
              </div>

              <div className="adm-field">
                <label className="adm-label">
                  Подтвердите пароль <span className="adm-req">*</span>
                </label>
                <Controller
                  control={control}
                  name="passwordConfirm"
                  rules={{
                    required: 'Повторите новый пароль',
                    validate: v => v === newPassword || 'Пароли не совпадают',
                  }}
                  render={({ field }) => (
                    <div className="adm-input-wrap">
                      <IconLock size={15} stroke={2} className="adm-input-icon" />
                      <Input
                        {...field}
                        type={showConfirm ? 'text' : 'password'}
                        className="adm-input adm-input-has-suffix"
                        placeholder="Повторите новый пароль"
                        autoComplete="new-password"
                      />
                      <span className="adm-input-suffix">
                        <button
                          type="button"
                          className="adm-pwd-btn"
                          onClick={() => setShowConfirm(v => !v)}
                        >
                          {showConfirm ? <IconEyeOff size={15} stroke={2} /> : <IconEye size={15} stroke={2} />}
                        </button>
                      </span>
                    </div>
                  )}
                />
                {errors.passwordConfirm && <div className="form-field-error">{errors.passwordConfirm.message}</div>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="adm-save" type="submit" disabled={saving}>
                  {saving ? <span className="adm-btn-spinner" /> : <IconCheck size={15} stroke={2.5} />}
                  Сменить пароль
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
        </div>
      </div>
      <Notify message={msg} onDone={clear} />
    </div>
  );
};

export { ProfilePage };
